import { Box, Flex, Text, TagBadge, CollapsibleSection, Markdown, type TagColor } from '@chatsdet/ui';
import { CopyablePre } from '../../../ui/components/CopyButton';
import type {
  SpecReviewAssumption,
  SpecReviewDataDependency,
  SpecReviewReportJson,
} from '../types';

// ── Badge helpers ────────────────────────────────────────────────────────────
// Pass/fail-shaped verdicts use the repo's blue/orange convention (never
// green/red); partial/conditional verdicts sit at yellow in between.

const ASSUMPTION_META: Record<SpecReviewAssumption['status'], { label: string; color: TagColor }> = {
  verified: { label: 'Verified', color: 'blue' },
  failed: { label: 'Failed', color: 'orange' },
  unverifiable: { label: 'Unverifiable', color: 'gray' },
};

const IDEMPOTENCY_META: Record<
  SpecReviewReportJson['idempotency']['verdict'],
  { label: string; color: TagColor }
> = {
  idempotent: { label: 'Idempotent', color: 'blue' },
  not_idempotent: { label: 'Not idempotent', color: 'orange' },
  conditional: { label: 'Conditional', color: 'yellow' },
};

const PROVENANCE_LABEL: Record<SpecReviewDataDependency['provenance'], string> = {
  pre_existing: 'Pre-existing',
  created_by_flow: 'Created by flow',
  external_system: 'External system',
  unknown: 'Unknown',
};

/**
 * Research tab content for the main view: the assumptions ledger,
 * idempotency verdict, and data dependencies — the feasibility groundwork
 * behind the spec review, given full-width reading room instead of the
 * cramped 380px sidebar it used to live in.
 */
export function SpecReviewResearch({ report }: { report: SpecReviewReportJson }) {
  const idempotency = IDEMPOTENCY_META[report.idempotency.verdict];

  return (
    <Box>
      <CollapsibleSection label="Assumptions" count={report.assumptions.length} defaultExpanded hideBorder>
        <Flex direction="column" gap="sm">
          {report.assumptions.map((assumption, i) => {
            const meta = ASSUMPTION_META[assumption.status];
            return (
              <Box key={i} className="border-t border-border/40 pt-2 first:border-t-0 first:pt-0">
                <Flex align="start" gap="xs">
                  <TagBadge name={meta.label} color={meta.color} hideDot />
                  <Text size="sm" className="leading-snug">
                    {assumption.assumption}
                  </Text>
                </Flex>
                <Text size="xs" className="mt-1 text-muted-foreground">
                  {assumption.evidence}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </CollapsibleSection>

      <Box className="border-t border-border/50 py-3">
        <Flex align="center" gap="xs">
          <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wide">
            Idempotency
          </Text>
          <TagBadge name={idempotency.label} color={idempotency.color} hideDot />
        </Flex>
        <Text size="xs" className="mt-1 text-muted-foreground">
          {report.idempotency.notes}
        </Text>
      </Box>

      <CollapsibleSection
        label="Data dependencies"
        count={report.dataDependencies.length}
        defaultExpanded
      >
        <Flex direction="column" gap="sm">
          {report.dataDependencies.map((dep, i) => (
            <Box key={i} className="border-t border-border/40 pt-2 first:border-t-0 first:pt-0">
              <Text size="sm" className="leading-snug">
                {dep.description}
              </Text>
              <Flex align="center" gap="xs" className="mt-1 flex-wrap">
                <Text size="xs" className="text-muted-foreground">
                  {PROVENANCE_LABEL[dep.provenance]}
                </Text>
                <span className="text-muted-foreground/50">·</span>
                <Text size="xs" className="text-muted-foreground">
                  Seedable in test: {dep.canSeedInTest ? 'yes' : 'no'}
                </Text>
              </Flex>
              {dep.notes && (
                <Text size="xs" className="mt-0.5 text-muted-foreground/80 italic">
                  {dep.notes}
                </Text>
              )}
            </Box>
          ))}
        </Flex>
      </CollapsibleSection>
    </Box>
  );
}

/**
 * Cleaned-spec tab content for the main view — the AI-rewritten spec at
 * comfortable full-width reading measure, with the existing copy affordance.
 */
export function SpecReviewCleanedSpec({ cleanedSpec }: { cleanedSpec: string | null }) {
  if (!cleanedSpec) {
    return (
      <Text size="sm" className="text-muted-foreground">
        No cleaned spec available.
      </Text>
    );
  }

  return (
    <CopyablePre value={cleanedSpec} label="cleaned spec" variant="surface">
      <Markdown className="prose prose-sm max-w-none text-foreground prose-p:text-foreground prose-li:text-foreground">
        {cleanedSpec}
      </Markdown>
    </CopyablePre>
  );
}
